/**
 * One typed HTTP client, one error contract.
 *
 * The predecessor had roughly 190 raw `fetch` call sites across 30 files with
 * four different error idioms: some checked `res.ok`, some checked a `success`
 * field in the body, some checked both, and a large number ended in
 * `.catch(() => {})`. That last one is why a failed mission save looked
 * identical to a successful one: the write went out, the server rejected it,
 * and the UI moved on. An operator flew a plan the aircraft had never received.
 *
 * The rules here follow from that:
 *
 *   - `request` never throws. Network failure, timeout, HTTP error and a
 *     malformed body all arrive as the same `ApiResult` shape, so a caller
 *     cannot accidentally handle only one of them.
 *   - Every result carries `status` and `latencyMs`, and every call is reported
 *     to the activity log. A write that failed is visible without anyone having
 *     remembered to log it.
 *   - The server error shape is unwrapped in exactly one place.
 */

import { API_PREFIX, REQUEST_TIMEOUT_MS } from '@/config';

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'invalid'
  | 'server'
  | 'aborted'
  | 'unknown';

export interface ApiError {
  kind: ApiErrorKind;
  /** Written for an operator to read. Never a raw stack or a bare status line. */
  message: string;
  /** Machine-readable code from the server body, when it sent one. */
  code: string | null;
  /** The decoded body, kept for callers that need field-level validation detail. */
  detail: unknown;
}

interface ApiOk<T> {
  ok: true;
  data: T;
  error: null;
  status: number;
  latencyMs: number;
}

interface ApiFail {
  ok: false;
  data: null;
  error: ApiError;
  status: number;
  latencyMs: number;
}

export type ApiResult<T> = ApiOk<T> | ApiFail;

export interface ActivityEntry {
  id: number;
  at: number;
  method: string;
  path: string;
  status: number;
  ok: boolean;
  latencyMs: number;
  /** Empty on success unless the caller supplied a label. */
  message: string;
  label?: string;
}

export type ActivityListener = (entry: ActivityEntry) => void;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Human label for the activity log, e.g. "ARM alpha-1". */
  label?: string;
  /** Set false for polling that would otherwise flood the log. */
  logged?: boolean;
  query?: Record<string, string | number | boolean | undefined>;
  /** Extra headers, for the CSRF token the refresh endpoint requires. */
  headers?: Record<string, string>;
}

type TokenProvider = () => string | null;
type UnauthorizedHandler = () => Promise<boolean>;

let tokenProvider: TokenProvider = () => null;
let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * Told the error kind of every completed request, or undefined on success.
 * A callback rather than an import because the outbox already depends on this
 * module's types, and a two-way import would be a cycle.
 */
type ReachabilityObserver = (kind: string | undefined) => void;
let onReachability: ReachabilityObserver | null = null;

export function setReachabilityObserver(observer: ReachabilityObserver | null): void {
  onReachability = observer;
}

/**
 * Where the bearer token comes from. Registered by the auth store rather than
 * imported from it, so this module stays free of store imports and the error
 * contract can be tested without mounting React.
 */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/**
 * Called once on a 401. Returning true means a fresh token is now available and
 * the request is worth retrying exactly once.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

const listeners = new Set<ActivityListener>();
let nextActivityId = 1;

/** Subscribe to every call the client makes. Returns an unsubscribe function. */
export function onActivity(listener: ActivityListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(entry: ActivityEntry): void {
  for (const listener of listeners) {
    // One bad listener must not fail the request that fed it.
    try {
      listener(entry);
    } catch {
      /* a logging sink is never allowed to break a command */
    }
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'invalid';
  if (status >= 500) return 'server';
  return 'unknown';
}

interface ErrorBody {
  message?: unknown;
  error?: unknown;
  detail?: unknown;
  code?: unknown;
}

/**
 * The one place a server error body is turned into a sentence.
 *
 * FastAPI sends `{detail: string}` for raised HTTPExceptions and
 * `{detail: [{loc, msg, type}, ...]}` for validation failures. Our own handlers
 * send `{code, message}`. All three end up here.
 */
function describeError(status: number, body: unknown): { message: string; code: string | null } {
  const fallback = `Request failed (HTTP ${status})`;
  if (typeof body === 'string' && body.trim()) {
    return { message: body.trim().slice(0, 400), code: null };
  }
  if (!body || typeof body !== 'object') {
    return { message: fallback, code: null };
  }

  const b = body as ErrorBody;
  const code = typeof b.code === 'string' ? b.code : null;

  for (const candidate of [b.message, b.error, b.detail]) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return { message: candidate.trim(), code };
    }
  }

  if (Array.isArray(b.detail)) {
    const parts = b.detail
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const entry = item as { loc?: unknown; msg?: unknown };
        const msg = typeof entry.msg === 'string' ? entry.msg : null;
        if (!msg) return null;
        const loc = Array.isArray(entry.loc) ? entry.loc.filter((p) => p !== 'body').join('.') : '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter((part): part is string => part !== null);
    if (parts.length) return { message: parts.join('; '), code };
  }

  return { message: fallback, code };
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = path.startsWith('/api/') || path.startsWith('http')
    ? path
    : `${API_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function decode(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text().catch(() => '');
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function fail(
  kind: ApiErrorKind,
  message: string,
  status: number,
  latencyMs: number,
  detail: unknown = null,
  code: string | null = null,
): ApiFail {
  return { ok: false, data: null, error: { kind, message, code, detail }, status, latencyMs };
}

async function attempt<T>(
  path: string,
  options: RequestOptions,
): Promise<{ result: ApiResult<T>; url: string }> {
  const method = options.method ?? 'GET';
  const url = buildUrl(path, options.query);
  const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromCaller);

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const token = tokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const startedAt = performance.now();
  try {
    const response = await fetch(url, {
      method,
      headers,
      // The refresh token lives in an httpOnly cookie, so every call carries it.
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    });

    const latencyMs = performance.now() - startedAt;
    const body = await decode(response);

    if (!response.ok) {
      const { message, code } = describeError(response.status, body);
      return {
        url,
        result: fail(kindForStatus(response.status), message, response.status, latencyMs, body, code),
      };
    }

    return {
      url,
      result: { ok: true, data: body as T, error: null, status: response.status, latencyMs },
    };
  } catch (err) {
    const latencyMs = performance.now() - startedAt;
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    if (aborted && options.signal?.aborted) {
      return { url, result: fail('aborted', 'Request cancelled', 0, latencyMs) };
    }
    if (aborted) {
      return {
        url,
        result: fail('timeout', `No response after ${Math.round(timeoutMs / 1000)}s`, 0, latencyMs),
      };
    }
    const message = err instanceof Error && err.message ? err.message : 'Network unreachable';
    return { url, result: fail('network', message, 0, latencyMs) };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }
}

/**
 * Perform one API call. Never throws, always reports, always the same shape.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  let { result, url } = await attempt<T>(path, options);

  // A single silent retry after a token refresh. Bounded to one attempt so an
  // expired session cannot turn one click into a refresh loop.
  if (!result.ok && result.error.kind === 'unauthorized' && onUnauthorized) {
    const refreshed = await onUnauthorized();
    if (refreshed) {
      ({ result, url } = await attempt<T>(path, options));
    }
  }

  // Every request is evidence about whether the server is reachable, which is
  // what the offline badge and the outbox flush both key off.
  onReachability?.(result.ok ? undefined : result.error.kind);

  if (options.logged !== false) {
    emit({
      id: nextActivityId++,
      at: Date.now(),
      method: options.method ?? 'GET',
      path: url,
      status: result.status,
      ok: result.ok,
      latencyMs: Math.round(result.latencyMs),
      message: result.ok ? '' : result.error.message,
      ...(options.label ? { label: options.label } : {}),
    });
  }

  return result;
}

/* The seams below exist for exactly one caller: a Server-Sent Events response
 * has to be read incrementally, and `request` decodes the whole body before it
 * returns — right for every command in the app, and precisely wrong for a
 * stream. The alternative was a second hand-rolled fetch that invented its own
 * auth header and skipped the activity log, which is the shape of the 190 call
 * sites this module was written to replace. */

/** The URL `request` would build for this path. */
export function resolveUrl(path: string): string {
  return buildUrl(path, undefined);
}

/** The headers `request` would send, bearer token included. */
export function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const token = tokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Record a call this module did not itself make, so it still reaches the
 * activity log and the offline badge. `errorKind` is undefined on success.
 */
export function reportCall(
  entry: Omit<ActivityEntry, 'id' | 'at'>,
  errorKind?: ApiErrorKind,
): void {
  onReachability?.(errorKind);
  emit({ id: nextActivityId++, at: Date.now(), ...entry });
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/** Test seam. Resets module state between cases. */
export function resetClientForTests(): void {
  listeners.clear();
  tokenProvider = () => null;
  onUnauthorized = null;
  nextActivityId = 1;
}
